import { Dispatch, SetStateAction, useState, useEffect, useRef, MutableRefObject } from "react";
import { Grid } from "@mui/material";
import _ from "underscore";
// @ts-ignore
import { StriimModal, StriimButton, StriimCircularLoader, StriimTypography, useStriimForm } from "@striim/striim-ui";

import Stepper from "src/modules/wizards/common/stepper/stepper";
import { styles } from "./select-tables-modal.styles";
import SelectSchemas from "../select-schemas/select-schemas";
import api from "core/api/api";
import ValidateSchemaView from "./components/validate-schema-view/validate-schema-view";
import {
    fetchTables,
    getDatabase,
    getStepsName,
    getTableNameWithCorrectDots,
    Steps,
    isOracleOrOjet
} from "./select-tables.utils";
import { SelectTablesWrapper } from "./components/index";
import { APIStatus, Schema } from "./schema-table";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import { SelectPdbContainer } from "./components/select-pdb/select-pdb";
import { ArrowForward } from "src/generic/icon/customIcons";
import growl from "app/components/common/growl";
import { useSelectTables } from "src/modules/flow-designer/vts/select-tables-context";
import { CONNECTION_FAILURE_ERROR, Tabs } from "src/modules/flow-designer/vts/selectTables";
import { ValidationListRef } from "./components/validation-list/validation-list";
import { getNameAndSchema } from "src/modules/wizards/services/wizards-methods-helper";

interface SelectTablesModalProps {
    showSelectTablesModal: boolean;
    setShowSelectTablesModal: Dispatch<SetStateAction<boolean>>;
    isEditFlow: boolean;
    formValues: Object;
    isCDC: boolean;
    dbType: string;
    metaObjectId?: string;
    setTables: (tables: string) => void;
    tablesRef: MutableRefObject<{
        tab: string;
        schemas: number;
        entities?: number;
        timestamp: number;
        data: any[];
    } | null>;
    adapterName: string;
    onError: (error: string) => void;
    onCancel: (hasConnectionFailed: boolean, error: string) => void;
}

const getDefaultStepNameInEditFlow = (
    isEditFlow: boolean,
    isAppAdapter: boolean,
    dbType: string,
    isCDC: boolean,
    Steps: Steps
): string => {
    const isSalesforceCDC = dbType === DB_PROVIDERS.SALESFORCE.id && isCDC;
    return isEditFlow
        ? isSalesforceCDC
            ? Steps.SelectTables?.name
            : isAppAdapter
            ? Steps.SelectSchemas?.name
            : Steps.SelectTables?.name
        : Steps.SelectSchemas.name;
};

const initialApiStatus = {
    isAPISuccessful: false,
    isAPICompleted: false,
    canRetry: false
};

const SelectTablesModal = ({
    showSelectTablesModal = false,
    setShowSelectTablesModal,
    isEditFlow,
    setTables,
    formValues,
    isCDC,
    dbType,
    metaObjectId,
    tablesRef,
    adapterName,
    onError,
    onCancel
}: SelectTablesModalProps) => {
    const [schemas, setSchemas] = useState<Schema[]>([]);
    const [selectedSchemas, setSelectedSchemas] = useState<Schema[]>([]);
    const [selectedSchemaTables, setSelectedSchemaTables] = useState<Schema[]>([]);
    const [activeStep, setActiveStep] = useState<number>(isEditFlow ? 1 : 0);
    const {
        currentSelection,
        setCurrentSelection,
        sourceInfo: { schemaType, entityType, isAppAdapter }
    } = useSelectTables();
    const [selectedSchemaTableColumns, setSelectedSchemaTableColumns] = useState<Schema[]>(
        isEditFlow ? currentSelection.data : []
    );
    const { setFieldValue } = useStriimForm();

    const [pdbList, setPdbList] = useState<string[]>([]);
    const isOracleType = isOracleOrOjet(dbType);
    const existingContainerName = currentSelection.containerName || null;
    const hasExistingPdb = isOracleType && !!existingContainerName;
    const [selectedPdb, setSelectedPdb] = useState<string | null>(existingContainerName);
    const initialShowPdbStep = isOracleType && (!isEditFlow || hasExistingPdb);
    const [showPdbStep, setShowPdbStep] = useState<boolean>(initialShowPdbStep);

    const Steps: Steps = getStepsName(schemaType, entityType, isAppAdapter, dbType, isCDC, showPdbStep);
    const [currentStepName, setCurrentStepName] = useState<string>(
        initialShowPdbStep && !isEditFlow
            ? "Select Pluggable Database"
            : getDefaultStepNameInEditFlow(isEditFlow, isAppAdapter, dbType, isCDC, Steps)
    );
    const [properties, setProperties] = useState<Object>(() => {
        const formatted = {};
        if (formValues) {
            for (const key in formValues) {
                if (Object.prototype.hasOwnProperty.call(formValues, key)) {
                    formatted[key.toLowerCase()] = formValues[key];
                }
            }
        }
        if (existingContainerName) {
            formatted["containername"] = existingContainerName;
            formatted["ispdb"] = true;
        }
        return formatted;
    });

    const [selectedSchema, setSelectedSchema] = useState<Schema>(schemas[0]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const schemaTablesList = useRef<Schema[]>();
    const schemaTablesColumnList = useRef<Schema[]>();

    schemaTablesList.current = selectedSchemaTables;
    schemaTablesColumnList.current = selectedSchemaTableColumns;

    const [apiStatus, setApiStatus] = useState<APIStatus>(initialApiStatus);

    const childRef = useRef<ValidationListRef>();
    const cancelRef = useRef(false);

    const StepNames = Object.values(Steps);

    const StepTitles = Object.values(Steps)
        .filter(v => !v.isTransient)
        .map(v => v.name);

    useEffect(() => {
        (async function() {
            try {
                setIsLoading(true);

                const formattedProperties = {};
                if (formValues) {
                    for (let key in formValues) {
                        if (Object.prototype.hasOwnProperty.call(formValues, key)) {
                            formattedProperties[key.toLowerCase()] = formValues[key];
                        }
                    }
                }
                if (existingContainerName) {
                    formattedProperties["containername"] = existingContainerName;
                    formattedProperties["ispdb"] = true;
                }

                if (isEditFlow && currentSelection.data?.length) {
                    // In edit flow, skip getDatabase and directly fetch tables for known schemas
                    setSelectedSchemas(currentSelection.data);
                    const previousData = currentSelection.data;

                    const fetchPromises = previousData.map(schema => {
                        return new Promise<Schema | null>(resolve => {
                            fetchTables(
                                schema.name,
                                formattedProperties,
                                dbType,
                                metaObjectId,
                                adapterName,
                                (schemaName: string, tables: string[]) => {
                                    const preSelectedSchema = previousData.find(s => s.name === schemaName);
                                    const isAnyTableSelected = preSelectedSchema?.tables?.some(
                                        (t: any) => t.isSelected
                                    );

                                    const fetchedTables: Schema["tables"] = [];
                                    _.each(tables, (table: string) => {
                                        let name: string;
                                        if (isAppAdapter) {
                                            name = table;
                                        } else {
                                            [name] = getNameAndSchema(table, dbType);
                                        }

                                        const preSelectedTable = preSelectedSchema?.tables?.find(
                                            (t: any) => t.name === name
                                        );
                                        fetchedTables.push({
                                            id: table,
                                            name,
                                            fullTableName: table,
                                            isValid: true,
                                            isSelected: preSelectedTable ? preSelectedTable.isSelected : false
                                        });
                                    });

                                    resolve({
                                        ...schema,
                                        isSelected: isAnyTableSelected,
                                        tables: fetchedTables
                                    });
                                },
                                () => {
                                    console.error("Failed to fetch tables for schema:", schema.name);
                                    resolve(null);
                                }
                            ).catch(() => {
                                resolve(null);
                            });
                        });
                    });

                    const results = await Promise.all(fetchPromises);
                    const updatedSchemas = results.filter(Boolean) as Schema[];
                    if (updatedSchemas.length) {
                        setSelectedSchemaTableColumns(updatedSchemas);
                    } else {
                        setShowSelectTablesModal(false);
                        onError(CONNECTION_FAILURE_ERROR);
                    }
                } else if (isOracleType) {
                    // For Oracle/OJet, defer schema fetching until PDB step completes
                    // The SelectPdbContainer will handle the CDB check and PDB list fetching
                } else {
                    const dbs = await getDatabase(formattedProperties, dbType, isCDC, metaObjectId, adapterName);
                    let data;

                    // WizardHelper APIs return {result, message} objects
                    if (typeof dbs === "object" && dbs !== null && !Array.isArray(dbs)) {
                        if (dbs.result === true) {
                            data = dbs.message ?? [];
                        } else {
                            setIsLoading(false);
                            setShowSelectTablesModal(false);
                            onError(CONNECTION_FAILURE_ERROR);
                            console.error(dbs);
                            return;
                        }
                    } else {
                        data = dbs;
                    }

                    const formattedSchemas = data.map((db, index) => ({
                        id: index,
                        name: db,
                        isSelected: false,
                        isDisabled: false,
                        tables: []
                    }));

                    setSchemas(formattedSchemas);
                    setSelectedSchema(formattedSchemas[0]);
                }
            } catch (error) {
                console.error(error);
                setShowSelectTablesModal(false);
                onError(CONNECTION_FAILURE_ERROR);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [formValues, isEditFlow]);

    useEffect(() => {
        if (currentStepName === Steps.FetchSchemas?.name && apiStatus.isAPISuccessful) {
            setTimeout(() => {
                handleNextClick();
            }, 500);
        }
    }, [currentStepName, apiStatus]);

    const currentStep = Object.values(Steps).find(step => step.name === currentStepName);

    const handleCancelClick = () => {
        onCancel(apiStatus.canRetry, CONNECTION_FAILURE_ERROR);
        setShowSelectTablesModal(false);
        cancelRef.current = true;
    };

    const fetchSchemas = async () => {
        try {
            setIsLoading(true);
            const dbs = await getDatabase(properties, dbType, isCDC, metaObjectId, adapterName);
            let data;

            if (typeof dbs === "object" && dbs !== null && !Array.isArray(dbs)) {
                if (dbs.result === true) {
                    data = dbs.message ?? [];
                } else {
                    onError(CONNECTION_FAILURE_ERROR);
                    return;
                }
            } else {
                data = dbs;
            }

            const previousSchemaNames = new Set(selectedSchemaTableColumns?.map((s: Schema) => s.name) || []);

            const formattedSchemas = data.map((db, index) => ({
                id: index,
                name: db,
                isSelected: false,
                isDisabled: false,
                tables: []
            }));

            setSchemas(formattedSchemas);
            setSelectedSchema(formattedSchemas[0]);

            // Pre-select schemas that were previously selected, matched by name
            const preSelected = formattedSchemas.filter(s => previousSchemaNames.has(s.name));
            if (preSelected.length) {
                setSelectedSchemas(preSelected);
            }
        } catch (error) {
            console.error(error);
            onError(CONNECTION_FAILURE_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePdbCheckComplete = (isPdb: boolean, pdbs: string[]) => {
        if (isPdb && pdbs.length > 0) {
            setShowPdbStep(true);
            setPdbList(pdbs);
        } else {
            setShowPdbStep(false);
            setCurrentStepName(Steps.SelectSchemas.name);
            fetchSchemas();
        }
    };

    const handleSelectPdb = (pdb: string) => {
        setSelectedPdb(pdb);
        setProperties(prev => ({ ...prev, containername: pdb, ispdb: true }));
    };

    const handleBackClick = () => {
        if (isEditFlow) {
            const schemasInEditFlow = selectedSchemaTableColumns?.map(schema => ({
                ...schema,
                tables: [],
                isSelected: schema.tables?.some(table => table.isSelected)
            }));
            setSelectedSchemas(schemasInEditFlow || []);
        }

        //Set the cancelRef to true, to stop invoking the apis on back click and reset api status
        if (currentStepName === Steps.FetchSchemas?.name) {
            cancelRef.current = true;
            setApiStatus({ isAPISuccessful: false, isAPICompleted: false, canRetry: false });
        }

        //Go back to 1st step - select schemas skipping Fetch schemas step
        const stepsToGoBackwards = currentStepName === Steps?.SelectTables?.name ? 2 : 1;
        const previousStep = StepNames.find(step => step.step === currentStep?.step - stepsToGoBackwards);
        if (previousStep.name === Steps.SelectTables.name || (previousStep?.isTransient && previousStep.step !== 0)) {
            setActiveStep(step => step - stepsToGoBackwards);
        }

        if (previousStep.name === Steps.SelectTables.name) {
            setSelectedSchema(selectedSchemaTables[0]);
        }

        // In edit flow, fetch schemas when navigating back to SelectSchemas for the first time
        if (previousStep?.name === Steps.SelectSchemas.name && schemas.length === 0) {
            fetchSchemas();
        }

        previousStep && setCurrentStepName(previousStep.name);
        setApiStatus({
            isAPISuccessful: false,
            isAPICompleted: false,
            canRetry: false
        });
    };

    const handleNextClick = () => {
        if (currentStep.isLastStep) {
            handleConfirmClick();
            return;
        }
        //Set the cancelRef to false, to reset it
        if (currentStepName === Steps.SelectSchemas.name) {
            cancelRef.current = false;
        }
        if (currentStepName === Steps.SelectPdb?.name) {
            fetchSchemas();
        }
        if (currentStepName === Steps.FetchSchemas?.name && apiStatus.canRetry) {
            childRef.current?.retryAPI();
        } else {
            const nextStep = StepNames.find(step => step.step === currentStep?.step + 1);
            if (!nextStep?.isTransient) {
                setActiveStep(step => step + 1);
            }

            if (nextStep?.name === Steps.FetchSchemas.name) {
                setSelectedSchemaTables(selectedSchemas);
            }
            if (nextStep?.name === Steps.SelectTables.name) {
                setSelectedSchemaTableColumns(selectedSchemaTables);
            }

            nextStep && setCurrentStepName(nextStep.name);
            setApiStatus({
                isAPISuccessful: false,
                isAPICompleted: false,
                canRetry: false
            });
        }
    };

    const handleConfirmClick = async () => {
        try {
            let tableNames: string, objects: string;
            let tables = [];
            const currentTimestamp = Date.now();
            if (isAppAdapter) {
                const isSalesforceCDC = isCDC && dbType === DB_PROVIDERS.SALESFORCE.id;
                if (isSalesforceCDC) {
                    //only one channel could be selected
                    const selectedObjects =
                        selectedSchemaTableColumns[0].tables
                            ?.filter(table => table.isSelected)
                            ?.map(table => table.name) || [];
                    objects = selectedObjects?.join(";") || "";
                    setTables(objects);
                    setCurrentSelection({
                        schemas: selectedSchemas.length,
                        entities: selectedObjects.length,
                        timestamp: currentTimestamp,
                        data: selectedSchemaTableColumns
                    });
                    tablesRef.current = {
                        tab: Tabs.visual,
                        schemas: selectedSchemas.length,
                        entities: selectedObjects.length,
                        timestamp: currentTimestamp,
                        data: selectedSchemaTableColumns
                    };
                } else {
                    objects = selectedSchemas.map(schema => schema.name).join(";");
                    setTables(objects);
                    setCurrentSelection({
                        schemas: selectedSchemas.length,
                        entities: null,
                        timestamp: currentTimestamp,
                        data: selectedSchemas
                    });
                    tablesRef.current = {
                        tab: Tabs.visual,
                        schemas: selectedSchemas.length,
                        entities: null,
                        timestamp: currentTimestamp,
                        data: selectedSchemas
                    };
                }

                setShowSelectTablesModal(false);
            } else {
                tables = selectedSchemaTableColumns.reduce((accumulated, current) => {
                    let tablesInSchema = [];
                    _.each(current.tables, selectedTable => {
                        if (!!selectedTable.isSelected) {
                            tablesInSchema.push(
                                getTableNameWithCorrectDots(selectedTable.fullTableName, properties, isCDC, dbType)
                            );
                        }
                    });
                    accumulated.push(...tablesInSchema);
                    return accumulated;
                }, []);

                api.getEscapedNames(tables).then(async escapedTables => {
                    if (
                        [DB_PROVIDERS.MONGODB.id, DB_PROVIDERS.COSMOSDB.id, DB_PROVIDERS.MONGO_COSMOSDB.id].includes(
                            dbType
                        )
                    ) {
                        tableNames = tables.join(",");
                    } else {
                        tableNames = escapedTables.join(";");
                    }
                    const selectedSchemas = selectedSchemaTableColumns.filter(
                        schema => !!schema.isSelected && !!schema.tables?.length
                    );
                    const selectedEntitiesCount: number = selectedSchemas.reduce((accumulated: number, current) => {
                        accumulated += current.tables?.filter(table => !!table?.isSelected)?.length || 0;
                        return accumulated;
                    }, 0);

                    const data = selectedSchemas.filter(schema => ({
                        ...schema,
                        tables: schema.tables.filter(table => table.isSelected)
                    }));

                    setTables(tableNames);
                    setCurrentSelection({
                        schemas: selectedSchemas.length,
                        entities: selectedEntitiesCount,
                        timestamp: currentTimestamp,
                        data
                    });
                    tablesRef.current = {
                        tab: Tabs.visual,
                        schemas: selectedSchemas.length,
                        entities: selectedEntitiesCount,
                        timestamp: currentTimestamp
                    };
                    const hasQuery = !!formValues.hasOwnProperty("Query");
                    const hasMultiQuery = !!formValues.hasOwnProperty("MultiQuery");
                    hasQuery && setFieldValue("Query", "");
                    hasMultiQuery && setFieldValue("MultiQuery", "");
                    setShowSelectTablesModal(false);
                });
            }
        } catch (error) {
            growl.error(error);
        }
    };

    const getActiveStepComponent = () => {
        switch (currentStepName) {
            case Steps.SelectPdb?.name:
                return (
                    <SelectPdbContainer
                        formValues={formValues}
                        dbType={dbType}
                        metaObjectId={metaObjectId}
                        adapterName={adapterName}
                        selectedPdb={selectedPdb}
                        cachedPdbList={pdbList}
                        onSelectPdb={handleSelectPdb}
                        onPdbCheckComplete={handlePdbCheckComplete}
                        onError={error => {
                            setShowSelectTablesModal(false);
                            onError(error);
                        }}
                    />
                );
            case Steps.SelectSchemas.name:
                return (
                    <SelectSchemas
                        schemas={schemas}
                        selectedSchemas={selectedSchemas}
                        setSelectedSchemas={setSelectedSchemas}
                        schemaType={schemaType}
                        isLoading={isLoading}
                    />
                );
            case Steps.FetchSchemas?.name:
                return (
                    <ValidateSchemaView
                        adapterName={adapterName}
                        selectedSchemaTables={selectedSchemaTables}
                        setSelectedSchemaTables={setSelectedSchemaTables}
                        selectedSchemaTableColumns={selectedSchemaTableColumns}
                        isEditFlow={isEditFlow}
                        schemaTablesList={schemaTablesList}
                        formValues={properties}
                        dbType={dbType}
                        metaObjectId={metaObjectId}
                        setApiStatus={setApiStatus}
                        childRef={childRef}
                        schemaType={schemaType}
                        cancelRef={cancelRef}
                    />
                );
            case Steps.SelectTables.name:
                return isLoading ? (
                    <Grid
                        container
                        width="100%"
                        height="100%"
                        minHeight={500}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Grid item display="flex" alignItems="center" gap={1}>
                            <StriimCircularLoader />
                            <StriimTypography variant="body1">Loading...</StriimTypography>
                        </Grid>
                    </Grid>
                ) : (
                    <SelectTablesWrapper
                        schemas={selectedSchemaTableColumns}
                        setSchemas={setSelectedSchemaTableColumns}
                    />
                );
            default:
                return null;
        }
    };

    const isNextDisabled = (currentStepName: string) => {
        switch (currentStepName) {
            case Steps.SelectPdb?.name:
                return !selectedPdb;
            case Steps.SelectSchemas.name:
                return !selectedSchemas.length;
            case Steps.FetchSchemas?.name:
                return !apiStatus.canRetry;
            case Steps.SelectTables.name:
                const isAnyTableSelected = selectedSchemaTableColumns.some(schema =>
                    schema.tables?.some(table => table?.isSelected)
                );
                return !isAnyTableSelected;
            default:
                return false;
        }
    };

    return (
        <StriimModal
            autoHeight={currentStepName === Steps.SelectTables?.name}
            size="large"
            onCancel={handleCancelClick}
            onConfirm={handleConfirmClick}
            isVisible={showSelectTablesModal}
            titleContent={<Stepper steps={StepTitles} activeStep={activeStep} />}
            dialogProps={{
                sx: [
                    styles.dialog,
                    currentStepName === Steps.SelectSchemas?.name || currentStepName === Steps.SelectPdb?.name
                        ? styles.dialogHeight
                        : ""
                ],
                "data-test-id": "select-schemas-modal"
            }}
            dialogTitleProps={{
                sx: styles.dialogTitle
            }}
            dialogContentProps={{
                sx: styles.dialogContent
            }}
            footerContent={
                <Grid container justifyContent={"flex-end"}>
                    <Grid item display={"flex"} gap={0.5}>
                        <StriimButton variant="text" onClick={handleCancelClick} data-testid="select-tables-cancel">
                            Cancel
                        </StriimButton>
                        {!(currentStepName === Steps.SelectPdb?.name) &&
                            !(currentStepName === Steps.SelectSchemas.name && !showPdbStep) && (
                                <StriimButton
                                    disabled={isLoading}
                                    variant="secondary"
                                    onClick={handleBackClick}
                                    data-testid="select-tables-back"
                                >
                                    Back
                                </StriimButton>
                            )}
                        <StriimButton
                            variant="primary"
                            endIcon={!currentStep.isLastStep && !apiStatus.canRetry ? <ArrowForward /> : null}
                            disabled={isLoading || isNextDisabled(currentStepName)}
                            onClick={handleNextClick}
                            data-testid="select-tables-next"
                        >
                            {currentStep.isLastStep
                                ? "Done"
                                : currentStep.name === Steps.FetchSchemas?.name && apiStatus.canRetry
                                ? "Retry"
                                : "Next"}
                        </StriimButton>
                    </Grid>
                </Grid>
            }
            dialogActionProps={{
                sx: styles.dialogAction
            }}
        >
            {getActiveStepComponent()}
        </StriimModal>
    );
};

export default SelectTablesModal;
